import React, { useState } from 'react';
import { 
  ArrowLeft, 
  ChevronDown, 
  ChevronRight,
  Users, 
  UtensilsCrossed, 
  CreditCard, 
  Settings, 
  Sparkles,
  UserPlus,
  FileText,
  Scale,
  RefreshCw,
  ClipboardList,
  ShoppingCart,
  Palette,
  HelpCircle,
  PlayCircle,
  Target,
  Zap,
  Download
} from 'lucide-react';

const HelpPage = ({ setCurrentView, subscription }) => {
  const [expandedSection, setExpandedSection] = useState('getting-started');

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <PlayCircle size={20} />,
      content: (
        <div className="help-content">
          <h4>Welcome to Plate Planner Pro!</h4>
          <p>Plate Planner Pro helps fitness coaches create personalized, AI-powered meal plans for their clients. Here's how to get started:</p>
          
          <div className="help-steps">
            <div className="help-step">
              <span className="step-number">1</span>
              <div>
                <strong>Add Your First Client</strong>
                <p>Click "Add Client" in the navigation and fill in their details including age, weight, height, activity level, and dietary requirements.</p>
              </div>
            </div>
            <div className="help-step">
              <span className="step-number">2</span>
              <div>
                <strong>Set Macro Targets</strong>
                <p>Configure their daily calorie target and macro split (protein, carbs, fat percentages). The app will use these to generate balanced meals.</p>
              </div>
            </div>
            <div className="help-step">
              <span className="step-number">3</span>
              <div>
                <strong>Generate a Meal Plan</strong>
                <p>From the client's profile, click "Generate Meal Plan". Our AI will create 3 sample options tailored to their goals.</p>
              </div>
            </div>
            <div className="help-step">
              <span className="step-number">4</span>
              <div>
                <strong>Export & Share</strong>
                <p>Download the meal plan as a branded PDF to share with your client via email or messaging.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'managing-clients',
      title: 'Managing Clients',
      icon: <Users size={20} />,
      content: (
        <div className="help-content">
          <h4>Client Management</h4>
          
          <div className="help-subsection">
            <h5><UserPlus size={16} /> Adding a New Client</h5>
            <p>Navigate to "Add Client" and complete the form:</p>
            <ul>
              <li><strong>Basic Info:</strong> Name, email, age, gender</li>
              <li><strong>Body Metrics:</strong> Current weight, height, goal weight</li>
              <li><strong>Activity Level:</strong> Sedentary, Lightly Active, Moderately Active, Very Active, or Extremely Active</li>
              <li><strong>Dietary Requirements:</strong> Restrictions (vegetarian, vegan, etc.), allergies, and food preferences</li>
              <li><strong>Meal Structure:</strong> Number of meals and snacks per day</li>
              <li><strong>Macro Split:</strong> Percentage of calories from protein, carbs, and fat</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5><Scale size={16} /> Tracking Weight Progress</h5>
            <p>On each client's profile, you can log weight entries to track their progress over time:</p>
            <ul>
              <li>Click "Add Weight Entry" on the client detail page</li>
              <li>Enter the weight and optionally add a note</li>
              <li>View progress on the weight chart showing their journey towards their goal</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Editing Client Information</h5>
            <p>Click the "Edit" button on any client's profile to update their details. Changes will apply to future meal plans - existing plans won't be affected.</p>
          </div>
        </div>
      )
    },
    {
      id: 'meal-plans',
      title: 'Creating Meal Plans',
      icon: <UtensilsCrossed size={20} />,
      content: (
        <div className="help-content">
          <h4>AI-Powered Meal Plan Generation</h4>
          
          <div className="help-subsection">
            <h5><Sparkles size={16} /> Generating a Meal Plan</h5>
            <p>From a client's profile:</p>
            <ol>
              <li>Click the <strong>"Generate Meal Plan"</strong> button</li>
              <li>Choose a template style:
                <ul>
                  <li><strong>Classic & Traditional:</strong> Familiar, home-style meals</li>
                  <li><strong>Quick & Simple:</strong> Fast prep, minimal ingredients</li>
                  <li><strong>Variety & Gourmet:</strong> Diverse, restaurant-quality dishes</li>
                </ul>
              </li>
              <li>Enable <strong>Calorie Cycling</strong> (Pro/Unlimited) if desired - select high calorie days for training</li>
              <li>Click "Generate" and wait for the AI to create 3 unique sample plans</li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5><RefreshCw size={16} /> Swapping Meals</h5>
            <p>Don't like a particular meal? Click the <strong>swap icon</strong> next to any meal to have the AI generate an alternative with similar macros.</p>
            <p className="help-note"><strong>Note:</strong> Starter plan allows 2 swaps per meal plan. Pro and Unlimited have unlimited swaps.</p>
          </div>

          <div className="help-subsection">
            <h5><Target size={16} /> Understanding the Meal Plan</h5>
            <p>Each meal plan includes:</p>
            <ul>
              <li><strong>3 Sample Options:</strong> Different meal combinations for variety</li>
              <li><strong>Daily Breakdown:</strong> Breakfast, lunch, dinner, and snacks</li>
              <li><strong>Macro Information:</strong> Calories, protein, carbs, and fat for each meal</li>
              <li><strong>Ingredients List:</strong> Everything needed for each recipe (in grams)</li>
              <li><strong>Cooking Instructions:</strong> Step-by-step preparation guide</li>
              <li><strong>Shopping List:</strong> Consolidated list of all ingredients (Pro/Unlimited)</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'custom-meals',
      title: 'Customizing Meals',
      icon: <Zap size={20} />,
      content: (
        <div className="help-content">
          <h4>Custom Meal Builder</h4>
          
          <div className="help-subsection">
            <h5>Editing a Meal</h5>
            <p>Click the <strong>edit icon</strong> on any meal to open the Custom Meal Builder:</p>
            <ul>
              <li>Modify the meal name</li>
              <li>Add, remove, or edit ingredients</li>
              <li>Adjust quantities (always in grams)</li>
              <li>Update cooking instructions</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5><Sparkles size={16} /> Automatic Macro Recalculation</h5>
            <p>When you edit ingredients and save, the AI automatically recalculates:</p>
            <ul>
              <li>Total calories for the meal</li>
              <li>Protein, carbs, and fat content</li>
              <li>Daily totals are updated accordingly</li>
            </ul>
            <p className="help-note">This ensures your client's macro targets stay accurate even after customizations.</p>
          </div>

          <div className="help-subsection">
            <h5>Adding a New Meal</h5>
            <p>You can add entirely new meals to any sample plan by clicking "Add Meal" and filling in the details manually or letting AI assist with macro calculations.</p>
          </div>
        </div>
      )
    },
    {
      id: 'quick-meal-plan',
      title: 'Quick Meal Plan',
      icon: <Zap size={20} />,
      proFeature: true,
      content: (
        <div className="help-content">
          <h4>Quick Meal Plan Generator</h4>
          <p className="feature-badge">Pro & Unlimited Feature</p>
          
          <div className="help-subsection">
            <h5>One-Off Meal Plans</h5>
            <p>Need a quick meal plan without creating a client? Use Quick Meal Plan:</p>
            <ol>
              <li>Click "Quick Meal Plan" from the dashboard</li>
              <li>Enter calorie target and number of meals</li>
              <li>Set macro percentages</li>
              <li>Add any dietary restrictions</li>
              <li>Generate instantly!</li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5>Use Cases</h5>
            <ul>
              <li>Quick consultations or demos</li>
              <li>Creating sample plans for social media</li>
              <li>Testing different macro splits</li>
              <li>Generating plans for yourself</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'templates',
      title: 'Meal Plan Templates',
      icon: <ClipboardList size={20} />,
      unlimitedFeature: true,
      content: (
        <div className="help-content">
          <h4>Meal Plan Templates</h4>
          <p className="feature-badge">Unlimited Plan Feature</p>
          
          <div className="help-subsection">
            <h5>Saving Templates</h5>
            <p>Save any meal plan as a reusable template:</p>
            <ol>
              <li>Open a meal plan you want to save</li>
              <li>Click "Save as Template"</li>
              <li>Give it a name and category (e.g., "High Protein Cut", "Vegan Bulk")</li>
              <li>Add a description for easy reference</li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5>Using Templates</h5>
            <p>Apply saved templates to any client:</p>
            <ol>
              <li>Go to a client's profile</li>
              <li>Click "Apply Template"</li>
              <li>Select from your saved templates</li>
              <li>The template's meals are adjusted to match the client's calorie target</li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5>Managing Templates</h5>
            <p>Access all your templates from the "Templates" section in the navigation. You can edit, duplicate, or delete templates as needed.</p>
          </div>
        </div>
      )
    },
    {
      id: 'exporting',
      title: 'Exporting & Sharing',
      icon: <Download size={20} />,
      content: (
        <div className="help-content">
          <h4>Exporting Meal Plans</h4>
          
          <div className="help-subsection">
            <h5><FileText size={16} /> PDF Export</h5>
            <p>Download professional PDF meal plans to share with clients:</p>
            <ol>
              <li>Open the meal plan you want to export</li>
              <li>Click "Export PDF" or the download icon</li>
              <li>Choose which sample(s) to include</li>
              <li>The PDF includes:
                <ul>
                  <li>Your branding (logo, company name, colors)</li>
                  <li>Client details and goals</li>
                  <li>Complete meal breakdown with macros</li>
                  <li>Ingredients and instructions</li>
                  <li>Shopping list (if enabled)</li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5><ShoppingCart size={16} /> Shopping Lists</h5>
            <p className="feature-badge">Pro & Unlimited Feature</p>
            <p>Each meal plan automatically generates a consolidated shopping list combining all ingredients. This makes it easy for clients to shop for their weekly meals.</p>
          </div>

          <div className="help-subsection">
            <h5><Palette size={16} /> Custom Branding</h5>
            <p className="feature-badge">Pro & Unlimited Feature</p>
            <p>Make your PDFs look professional with custom branding:</p>
            <ul>
              <li>Upload your logo</li>
              <li>Set your company name</li>
              <li>Choose primary and secondary colors</li>
            </ul>
            <p>Configure branding in Settings → Branding.</p>
          </div>
        </div>
      )
    },
    {
      id: 'billing',
      title: 'Billing & Plans',
      icon: <CreditCard size={20} />,
      content: (
        <div className="help-content">
          <h4>Subscription Plans</h4>
          
          <div className="help-subsection">
            <h5>Available Plans</h5>
            <table className="help-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Starter (£9)</th>
                  <th>Pro (£19)</th>
                  <th>Unlimited (£49)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Client Profiles</td>
                  <td>10</td>
                  <td>50</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>AI Meal Plans</td>
                  <td>✓</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Meal Swaps</td>
                  <td>2 per plan</td>
                  <td>Unlimited</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Quick Meal Plan</td>
                  <td>✗</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Custom Branding</td>
                  <td>✗</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Shopping Lists</td>
                  <td>✗</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Calorie Cycling</td>
                  <td>✗</td>
                  <td>✓</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Templates</td>
                  <td>✗</td>
                  <td>✗</td>
                  <td>✓</td>
                </tr>
                <tr>
                  <td>Team Members</td>
                  <td>✗</td>
                  <td>✗</td>
                  <td>Up to 3</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="help-subsection">
            <h5>Changing Plans</h5>
            <p>You can upgrade or downgrade your plan at any time from the Billing section. When you change plans:</p>
            <ul>
              <li><strong>Upgrading:</strong> You're charged the pro-rata difference immediately and gain instant access to new features</li>
              <li><strong>Downgrading:</strong> Changes take effect at the end of your current billing period</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Free Trial</h5>
            <p>New users get a <strong>7-day free Pro trial</strong> - no credit card required! This gives you full access to Pro features so you can experience the full power of Plate Planner Pro.</p>
          </div>
        </div>
      )
    },
    {
      id: 'team',
      title: 'Team Members',
      icon: <Users size={20} />,
      unlimitedFeature: true,
      content: (
        <div className="help-content">
          <h4>Team Management</h4>
          <p className="feature-badge">Unlimited Plan Feature</p>
          
          <div className="help-subsection">
            <h5>Adding Team Members</h5>
            <p>Share access with up to 3 team members (coaches, assistants):</p>
            <ol>
              <li>Go to Settings → Team Members</li>
              <li>Click "Add Team Member"</li>
              <li>Enter their name, email, and create a password for them</li>
              <li>They can now log in and access shared clients</li>
            </ol>
          </div>

          <div className="help-subsection">
            <h5>Team Member Permissions</h5>
            <p>Team members can:</p>
            <ul>
              <li>✓ View all clients</li>
              <li>✓ Create and edit meal plans</li>
              <li>✓ Export PDFs</li>
              <li>✓ Track client progress</li>
            </ul>
            <p>Team members cannot:</p>
            <ul>
              <li>✗ Access billing settings</li>
              <li>✗ Manage team members</li>
              <li>✗ Change branding settings</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Removing Team Members</h5>
            <p>You can remove team members at any time from Settings. Their access is revoked immediately, but any meal plans they created remain in the system.</p>
          </div>
        </div>
      )
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: <Settings size={20} />,
      content: (
        <div className="help-content">
          <h4>App Settings</h4>
          
          <div className="help-subsection">
            <h5><Palette size={16} /> Branding Settings</h5>
            <p className="feature-badge">Pro & Unlimited Feature</p>
            <p>Customize how your exported PDFs look:</p>
            <ul>
              <li><strong>Company Logo:</strong> Upload your logo (recommended 300x100px). Appears at the top of PDFs.</li>
              <li><strong>Company Name:</strong> Displayed as the header on meal plan PDFs</li>
              <li><strong>Primary Color:</strong> Used for headings and table headers</li>
              <li><strong>Secondary Color:</strong> Used for accents and highlights</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Account Information</h5>
            <p>View your account details including:</p>
            <ul>
              <li>Email address</li>
              <li>Current plan and status</li>
              <li>Trial days remaining (if applicable)</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'tips',
      title: 'Tips & Best Practices',
      icon: <HelpCircle size={20} />,
      content: (
        <div className="help-content">
          <h4>Pro Tips for Success</h4>
          
          <div className="help-subsection">
            <h5>Getting Better Meal Plans</h5>
            <ul>
              <li><strong>Be specific with preferences:</strong> Add detailed food preferences like "prefers chicken over beef" or "enjoys Mediterranean cuisine"</li>
              <li><strong>List all allergies:</strong> Even minor intolerances should be noted</li>
              <li><strong>Accurate activity level:</strong> This significantly affects calorie calculations</li>
              <li><strong>Realistic meal counts:</strong> If a client struggles with 6 meals, start with 4</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Working with Clients</h5>
            <ul>
              <li><strong>Generate multiple options:</strong> Create 2-3 meal plans and let clients choose their favorite</li>
              <li><strong>Use calorie cycling:</strong> Great for clients with varied training schedules</li>
              <li><strong>Track progress regularly:</strong> Weekly weigh-ins help you adjust plans as needed</li>
              <li><strong>Save templates:</strong> When you find a plan that works well, save it for similar clients</li>
            </ul>
          </div>

          <div className="help-subsection">
            <h5>Time-Saving Tips</h5>
            <ul>
              <li><strong>Use templates:</strong> Build a library of templates for common client types</li>
              <li><strong>Quick Meal Plan:</strong> Perfect for initial consultations or demos</li>
              <li><strong>Batch export:</strong> Prepare multiple client PDFs in one session</li>
              <li><strong>Team members:</strong> Delegate meal plan creation to assistants</li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="help-view">
      <div className="page-header">
        <h2><HelpCircle size={24} style={{marginRight: '8px'}} />Help Center</h2>
        <button className="btn-back" onClick={() => setCurrentView('home')}><ArrowLeft size={16} /> Back</button>
      </div>

      <p className="help-intro">
        Welcome to Plate Planner Pro! Browse the sections below to learn how to get the most out of the app.
      </p>

      <div className="help-sections">
        {helpSections.map((section) => (
          <div key={section.id} className={`help-section ${expandedSection === section.id ? 'expanded' : ''}`}>
            <button 
              className="help-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <div className="help-section-title">
                {section.icon}
                <span>{section.title}</span>
                {section.proFeature && <span className="help-feature-tag pro">Pro</span>}
                {section.unlimitedFeature && <span className="help-feature-tag unlimited">Unlimited</span>}
              </div>
              {expandedSection === section.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            {expandedSection === section.id && (
              <div className="help-section-body">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="help-footer">
        <p>Need more help? Contact us at <a href="mailto:support@plateplannerpro.com">support@plateplannerpro.com</a></p>
      </div>
    </div>
  );
};

export default HelpPage;
